import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, formatInr, type LedgerAccount } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

type RowState = { debit: string; credit: string };

export default function AccountingOpeningBalancesPage() {
  const { hospitalId, canPost } = useAccountingHospitalContext();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [voucherDate, setVoucherDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [amounts, setAmounts] = useState<Record<string, RowState>>({});

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ["accounting-accounts", hospitalId],
    queryFn: () => accountingApi.listLedgerAccounts(hospitalId),
    enabled: !!hospitalId,
  });

  const postable = useMemo(
    () =>
      (accounts as LedgerAccount[]).filter(
        (a) => a.isPostable && a.active && a.code !== "3200"
      ),
    [accounts]
  );

  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const a of postable) {
      const row = amounts[a.id];
      if (!row) continue;
      debit += Number(row.debit) || 0;
      credit += Number(row.credit) || 0;
    }
    return {
      debit: Math.round(debit * 100) / 100,
      credit: Math.round(credit * 100) / 100,
      diff: Math.round((debit - credit) * 100) / 100,
    };
  }, [amounts, postable]);

  const saveMut = useMutation({
    mutationFn: async (): Promise<{ voucher?: { id: string } }> => {
      const lines = postable
        .map((a) => {
          const row = amounts[a.id];
          const debit = Number(row?.debit) || 0;
          const credit = Number(row?.credit) || 0;
          if (!debit && !credit) return null;
          return { ledgerAccountId: a.id, debit, credit };
        })
        .filter(Boolean) as Array<{
        ledgerAccountId: string;
        debit: number;
        credit: number;
      }>;
      return accountingApi.openingBalances(
        {
          voucherDate,
          narration: "Opening balances",
          postImmediately: true,
          lines,
        },
        hospitalId
      ) as Promise<{ voucher?: { id: string } }>;
    },
    onSuccess: (r) => {
      toast.success("Opening balances posted (auto-balanced to 3200 if needed)");
      qc.invalidateQueries({ queryKey: ["accounting-vouchers", hospitalId] });
      qc.invalidateQueries({ queryKey: ["accounting-tb", hospitalId] });
      if (r?.voucher?.id) navigate(`../vouchers/${r.voucher.id}`);
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Failed to post openings"),
  });

  if (!canPost) {
    return (
      <p className="text-sm text-slate-600">
        Opening balances require post permission.{" "}
        <Link className="underline" to="..">
          Back
        </Link>
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium">Opening balances</h2>
        <p className="text-sm text-slate-500">
          Enter debit or credit per account. Any difference is auto-posted to Retained
          Earnings (3200).
        </p>
      </div>
      <div className="w-48 space-y-1">
        <Label>As on date</Label>
        <Input
          type="date"
          value={voucherDate}
          onChange={(e) => setVoucherDate(e.target.value)}
        />
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Account</TableHead>
              <TableHead className="w-36">Debit</TableHead>
              <TableHead className="w-36">Credit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4}>Loading accounts…</TableCell>
              </TableRow>
            ) : (
              postable.map((a) => {
                const row = amounts[a.id] || { debit: "", credit: "" };
                return (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono text-sm">{a.code}</TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>
                      <Input
                        inputMode="decimal"
                        value={row.debit}
                        onChange={(e) =>
                          setAmounts((prev) => ({
                            ...prev,
                            [a.id]: {
                              debit: e.target.value,
                              credit: e.target.value ? "" : row.credit,
                            },
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        inputMode="decimal"
                        value={row.credit}
                        onChange={(e) =>
                          setAmounts((prev) => ({
                            ...prev,
                            [a.id]: {
                              credit: e.target.value,
                              debit: e.target.value ? "" : row.debit,
                            },
                          }))
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div>
          Debit {formatInr(totals.debit)} · Credit {formatInr(totals.credit)} · Diff{" "}
          {formatInr(Math.abs(totals.diff))}
          {totals.diff !== 0 ? " → 3200" : ""}
        </div>
        <Button
          disabled={saveMut.isPending || (totals.debit === 0 && totals.credit === 0)}
          onClick={() => saveMut.mutate()}
        >
          Post opening balances
        </Button>
      </div>
    </div>
  );
}
